/* eslint-disable no-unused-vars */
import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import './App.css'

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom'
import { ThemeProvider } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import { useIdleTimer } from 'react-idle-timer'
import CssBaseline from '@material-ui/core/CssBaseline'
import Hidden from '@material-ui/core/Hidden'
import { PrivateRoute } from './app/helpers'
import { navigation } from './app/navigation/navigation.constants'
import { trackEvent } from './app/helpers/analytics'
import { Login } from './app/features/login/Login'
import {
  selectFetchNGUserInfoStatus,
  selectUserLoginStatusMessage,
} from './app/features/login/loginSlice'
import { theme } from './app/theme/theme'
import TopAppBar from './app/navigation/topAppBar/TopAppBar'
import BottomTabBar from './app/navigation/bottomTabBar/BottomTabBar'
import SideNav from './app/navigation/sideNav/SideNav'
import AdminPage from './app/features/admin/AdminPage'
import {
  fetchProfileLogEvents,
  resetProfileLogEvents,
} from './app/features/dashboard/widgets/MyProfile/myProfileSlice'
import IdleTimer from './app/features/IdleTimer/IdleTimer'
import {
  fetchSettings,
  selectDarkMode,
} from './app/features/settings/settingsSlice'
import PrivacyNotice from './app/features/settings/pages/PrivacyNotice'
import skipStyles from './skipStyles'
import MyMapTwo from './app/features/maps/MyMapTwo'
import { initializeGA4UserProperties } from './app/helpers/ga4UserSegmentation'

function PageviewTracker() {
  const location = useLocation()
  const dispatch = useDispatch()

  React.useEffect(() => {
    trackEvent(dispatch, {
      eventName: 'uxp_page_view',
      module: 'UXP Shell',
      object: 'Page',
      action: 'View',
      actionContext: 'PageRouting',
      actionContextValue: location.pathname,
    })
  }, [dispatch, location.pathname])

  return null
}

function isNetIDLoginLink(link) {
  if (!link) {
    return false
  }

  const loginLink = process.env.REACT_APP_LOGIN_LINK || ''
  const rawHref = link.getAttribute('href') || ''
  const fullHref = link.href || ''
  const text = link.textContent || ''

  if (text.includes('NetID Login')) {
    return true
  }

  if (!loginLink) {
    return false
  }

  if (rawHref === loginLink || fullHref === loginLink) {
    return true
  }

  try {
    return fullHref === new URL(loginLink, window.location.origin).href
  } catch (error) {
    return false
  }
}

function App() {
  const classes = skipStyles()
  const timeout = 28800000 // 8 hours in milliseconds

  const [remaining, setRemaining] = React.useState(timeout)
  const [openAlert, setOpenAlert] = React.useState(false)

  const dispatch = useDispatch()
  const { userProfile, userEvents } = useSelector((s) => s.myProfile)
  const fetchUserStatus = useSelector(selectFetchNGUserInfoStatus)
  const loginStatusMessage = useSelector(selectUserLoginStatusMessage)
  const darkMode = useSelector(selectDarkMode)

  React.useEffect(() => {
    const handleNetIDLoginClick = (event) => {
      const { target } = event

      if (!target || typeof target.closest !== 'function') {
        return
      }

      const link = target.closest('a')

      if (!isNetIDLoginLink(link)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const loginUrl = link.href || process.env.REACT_APP_LOGIN_LINK

      if (process.env.NODE_ENV === 'development') {
        console.log('NETID LOGIN CLICK TRACKING FIRED FROM APP.JS')
      }

      sessionStorage.setItem('netid_login_attempt_started', 'true')
      sessionStorage.removeItem('netid_login_success_tracked')
      sessionStorage.removeItem('netid_login_failure_tracked')

      trackEvent(dispatch, {
        eventName: 'uxp_login_click',
        module: 'UXP Shell',
        object: 'Login',
        action: 'Click',
        actionContext: 'Login',
        actionContextValue: 'NetIDLogin',
        loginMethod: 'NetID',
        route: window.location.pathname,
        page: window.location.pathname,
        url: window.location.href,
      })

      setTimeout(() => {
        window.location.href = loginUrl
      }, 500)
    }

    document.addEventListener('click', handleNetIDLoginClick, true)

    return () => {
      document.removeEventListener('click', handleNetIDLoginClick, true)
    }
  }, [dispatch])

  // Global app click tracker - catches app clicks at root level as safety net
  React.useEffect(() => {
    const handleGlobalAppClick = (event) => {
      const { target } = event

      if (!target || typeof target.closest !== 'function') {
        return
      }

      // Look for app-related click elements
      const appCard = target.closest(
        '[data-app-id], [class*="App"], [class*="app"]',
      )

      if (!appCard || !appCard.getAttribute('data-app-id')) {
        return
      }

      const appId = appCard.getAttribute('data-app-id')
      const appTitle = appCard.getAttribute('data-app-title') || appId

      if (process.env.NODE_ENV === 'development') {
        console.log('GLOBAL APP CLICK TRACKING FIRED FROM APP.JS', appTitle)
      }
    }

    document.addEventListener('click', handleGlobalAppClick, true)

    return () => {
      document.removeEventListener('click', handleGlobalAppClick, true)
    }
  }, [dispatch])

  const onPrompt = () => {
    trackEvent(dispatch, {
      eventName: 'uxp_session_idle',
      module: 'Session',
      object: 'UserSession',
      action: 'Idle',
      actionContext: 'IdleTimer',
    })
    setOpenAlert(true)
  }

  const onAction = () => {
    trackEvent(dispatch, {
      eventName: 'uxp_session_resume',
      module: 'Session',
      object: 'userSession',
      action: 'Resume',
      actionContext: 'IdleTimer',
    })
    // check if session cookie exists
    // if (
    //   window.location.hostname !== 'localhost' &&
    //   document.cookie.indexOf('myrJwtToken') === -1
    // ) {
    //   setOpenAlert(true)
    // }
    setRemaining(timeout)
  }

  const { getRemainingTime } = useIdleTimer({
    onIdle: onPrompt,
    onAction,
    timeout: remaining || timeout,
    debounce: 1000,
  })

  //   const { data } = useSelector((s) => s.settings)
  React.useEffect(() => {
    trackEvent(dispatch, {
      eventName: 'uxp_Shell_View',
      module: 'uxp Shell',
      object: 'Shell',
      action: 'View',
      actionContext: 'Shell',
      actionContextValue: 'ShellLoaded',
    })
  }, [dispatch])
  // Global widget link click tracker - intercepts all window.open calls
  React.useEffect(() => {
    const originalWindowOpen = window.open
    window.open = function (url, target, ...args) {
      if (url && typeof url === 'string') {
        trackEvent(dispatch, {
          eventName: 'uxp_widget_link_click',
          module: 'Dashboard',
          object: 'Widget',
          action: 'LinkClick',
          actionContext: 'WidgetLink',
          actionContextValue: url.substring(0, 50),
          url,
          appSource: 'WidgetLinkClick',
        })
      }
      return originalWindowOpen.call(window, url, target, ...args)
    }
    return () => {
      window.open = originalWindowOpen
    }
  }, [dispatch])

  React.useEffect(() => {
    trackEvent(dispatch, {
      eventName: 'uxp_Shell_ready',
      module: 'UXP Shell',
      object: 'Shell',
      action: 'Ready',
      actionContext: 'Shell',
      actionContextValue: 'ShellReady',
    })
  }, [dispatch])

  React.useEffect(() => {
    const hasSegmentation =
      userProfile &&
      (userProfile.isStudent ||
        userProfile.isStaff ||
        userProfile.isFaculty ||
        userProfile.isEmployeee ||
        userProfile.department ||
        userProfile.affiliation ||
        userProfile.locationCdDesc ||
        userProfile.isNB ||
        userProfile.isNW ||
        userProfile.isCM)
    if (
      hasSegmentation &&
      !sessionStorage.getItem('uxp_login_success_tracked')
    ) {
      trackEvent(dispatch, {
        eventName: 'uxp_login_success',
        module: 'UXP Shell',
        object: 'Login',
        action: 'Success',
        actionContext: 'Login',
        actionContextValue: 'UserLoggedIn',
        loginMethod: 'NetID',
        route: window.location.pathname,
        page: window.location.pathname,
        url: window.location.href,
      })

      sessionStorage.setItem('uxp_login_success_tracked', 'true')
    }
  }, [userProfile, dispatch])

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (window.location.hostname !== 'localhost') {
        if (userEvents?.length > 0) {
          dispatch(fetchProfileLogEvents()).then(() => {
            // clear events array after to events
            dispatch(resetProfileLogEvents())
          })
        }
      }
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [dispatch, userEvents])

  React.useEffect(() => {
    dispatch(fetchSettings())
  }, [dispatch])
  // Initialize GA4 user properties when user logs in
  React.useEffect(() => {
    if (userProfile && userProfile.email) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Initializing GA4 user properties for:', userProfile.email)
      }
      initializeGA4UserProperties(userProfile)
    }
  }, [userProfile?.email])

  return (
    <div>
      <ThemeProvider theme={theme(useSelector(selectDarkMode))}>
        <a href="#main-content" tabIndex="0" className={classes.skipLink}>
          Skip to main content
        </a>
        {openAlert === true ? (
          <IdleTimer
            openAlert={openAlert}
            dispatch={dispatch}
            userProfile={userProfile}
          />
        ) : null}
        <div>
          <CssBaseline />
          <Router basename={process.env.REACT_APP_BASENAME}>
            <TopAppBar />
            <Box height="100%">
              <Hidden smDown>
                <SideNav />
              </Hidden>
              <Switch>
                <Route exact component={Login} path="/login" />
                <Route exact component={AdminPage} path="/admin" />
                <Route exact component={MyMapTwo} path="/maps" />
                <Route
                  exact
                  component={PrivacyNotice}
                  path="/settings/appinfo/privacyNotices"
                />
                {navigation.routes.map((props) => (
                  <PrivateRoute
                    key={props.path}
                    exact={props.exact} // Needs to be false for nested navigation
                    path={props.path}
                    component={props.component}
                    {...props}
                  />
                ))}
                <Redirect from="*" to="/" />
              </Switch>
            </Box>
            <Hidden mdUp>
              <BottomTabBar />
            </Hidden>
          </Router>
        </div>
      </ThemeProvider>
    </div>
  )
}

export default App
